// Facts the size sheet shows beside the sizes: the style's department and GST,
// pieces of a variant already in the bag, and the size the customer usually
// takes (learned when their bills were loaded in the customer panel).

// customerId → sizes bought, most often first ([{ size, qty, category }]). Kept for the
// session only; the customer panel fills it from the customer's bill lines.
const boughtSizes = new Map();

export function rememberUsualSizes(customerId, sizes) {
  if (!customerId) return;
  if (Array.isArray(sizes) && sizes.length > 0) boughtSizes.set(String(customerId), sizes);
}

export function forgetUsualSizes() {
  boughtSizes.clear();
}

// The customer's usual size per category, most-bought category first:
// [{ category, size }] (category null when the product had none). Empty when
// their bills aren't loaded. One size across categories would be wrong (an M
// shirt and a 32 jean), so each category keeps its own.
export function usualSizes(customer, max = 2) {
  const bought = boughtSizes.get(String(customer?.cwrCustomerId || ""));
  if (!bought) return [];
  const seen = new Set();
  const out = [];
  bought.forEach((b) => {
    const key = b.category || "";
    if (seen.has(key) || out.length >= max) return;
    seen.add(key);
    out.push({ category: b.category || null, size: b.size });
  });
  return out;
}

// "Shirts M · Jeans 32" for the bag, or "" when nothing is known.
export function usualSizeLine(customer, max = 2) {
  return usualSizes(customer, max)
    .map((u) => (u.category ? `${u.category} ${u.size}` : String(u.size)))
    .join(" · ");
}

// The most-bought size that this style is made in, or null. With the style's
// category, only sizes bought in that category count (a 32 in jeans says
// nothing about a shirt); sizes with no recorded category count everywhere.
export function usualSizeIn(customer, styleSizes, category = null) {
  const bought = boughtSizes.get(String(customer?.cwrCustomerId || ""));
  if (!bought || !Array.isArray(styleSizes)) return null;
  const pool = category ? bought.filter((b) => !b.category || b.category === category) : bought;
  const hit = pool.find((b) => styleSizes.some((s) => String(s).toLowerCase() === String(b.size).toLowerCase()));
  return hit ? hit.size : null;
}

// Pieces of this product on the bill (sale lines only).
export function bagCount(items, productId) {
  if (!productId) return 0;
  return (items || []).reduce((n, item) => {
    if (item?.productId !== productId) return n;
    const qty = Number(item.weight ?? item.qty);
    return qty > 0 ? n + qty : n;
  }, 0);
}

// { category, gst } from the style's first variant ("Shirts", 5), each null when unknown.
export function sheetMeta(style) {
  const product = style?.variants?.[0]?.product;
  const category = String(product?.productCategoryName || "").trim() || null;
  const rate = Number(product?.taxRate);
  return { category, gst: Number.isFinite(rate) && rate > 0 ? rate : null };
}
